import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	endRemix,
	sendRemixEvent,
	startRemix,
	useMachinePhase,
} from '@atlassian/malleable-ui-components/state/machine';
import type { RemixExplicitFlowEndReason } from '@atlassian/malleable-ui-components/state/machine/remix-command-bus';

type PendingIntent = 'preview-opened' | 'submitted' | undefined;
type AdmissionRejectionReason = 'active-flow' | 'error';
type ShadowOperation =
	| 'endRemix'
	| 'notifyAdmissionRejected'
	| 'sendPreviewOpened'
	| 'sendSubmissionEvents'
	| 'sendTerminalEvent'
	| 'sendUnmountCancelled'
	| 'startRemix';

type StartRendererRemixArgs = {
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	source?: InteractionSource;
};

type RemixAdmission = {
	conversationId: string;
	admitted: boolean;
};

export type RendererRemixMachineLifecycle = {
	/** Machine notifications are shadow-only, non-throwing, and never execute renderer behavior. */
	startFlow: (args: StartRendererRemixArgs) => RemixAdmission;
	onPreviewOpened: () => void;
	onSubmitted: () => void;
	onPreviewDismissed: () => void;
	onStopThinking: () => void;
	onRovoAborted: () => void;
	onErrorDismissed: () => void;
	onSendRejected: () => void;
	onNavigation: () => void;
};

/** Adapts renderer UI intent to the Remix state machine. */
export const useRendererRemixMachineLifecycle = ({
	onAdmissionRejected,
}: {
	onAdmissionRejected?: (reason: AdmissionRejectionReason) => void;
} = {}): RendererRemixMachineLifecycle => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const logShadowOperationError = useCallback(
		(operation: ShadowOperation, error: unknown) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'rendererRemixShadowOperation',
					source: 'rendererRemix',
					attributes: {
						operation,
						errorName: error instanceof Error ? error.name : typeof error,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	/**
	 * Transitional containment for renderer write-shadow only.
	 * Remove or replace with explicit failure handling before CCCEMAU-3480 read cut-over,
	 * when machine state becomes authoritative.
	 */
	const runShadowSafely = useCallback(
		(operationName: ShadowOperation, operation: () => void): boolean => {
			try {
				operation();
				return true;
			} catch (error) {
				logShadowOperationError(operationName, error);
				return false;
			}
		},
		[logShadowOperationError],
	);

	const [remixId, setRemixId] = useState<string>();
	const phase = useMachinePhase(remixId ?? '');
	const phaseRef = useRef(phase);
	phaseRef.current = phase;
	const remixIdRef = useRef<string | undefined>(undefined);
	const pendingIntentRef = useRef<PendingIntent>(undefined);
	const finalizedRef = useRef(true);

	const releaseLocalFlow = useCallback(() => {
		finalizedRef.current = true;
		remixIdRef.current = undefined;
		pendingIntentRef.current = undefined;
	}, []);
	const safelyEndRemix = useCallback(
		(id: string, reason: RemixExplicitFlowEndReason) => {
			runShadowSafely('endRemix', () => endRemix(id, reason));
		},
		[runShadowSafely],
	);

	const endWith = useCallback(
		({
			event,
			reason,
		}: {
			event: 'GENERATION_ABORTED' | 'GENERATION_ERROR' | 'CANCELLED';
			reason: RemixExplicitFlowEndReason;
		}) => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}

			// Clear first so re-entrant modal callbacks cannot finalize twice.
			releaseLocalFlow();
			runShadowSafely('sendTerminalEvent', () => sendRemixEvent(activeRemixId, { type: event }));
			safelyEndRemix(activeRemixId, reason);
		},
		[releaseLocalFlow, runShadowSafely, safelyEndRemix],
	);

	const startFlow = useCallback(
		({ dynamicUiType, dynamicUiSubtype, source }: StartRendererRemixArgs): RemixAdmission => {
			const conversationId = uuidv4();

			// Preserve any existing admitted flow. SINGLE_ACTIVE_POLICY owns rejection.
			if (remixIdRef.current && !finalizedRef.current) {
				runShadowSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
				return { conversationId, admitted: false };
			}

			try {
				const result = startRemix({
					kind: 'create',
					conversationId,
					metadata: { dynamicUiType, dynamicUiSubtype, source },
				});

				if (!result) {
					runShadowSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
					return { conversationId, admitted: false };
				}

				finalizedRef.current = false;
				remixIdRef.current = result.remixId;
				pendingIntentRef.current = undefined;
				setRemixId(result.remixId);
				return { conversationId, admitted: true };
			} catch (error) {
				logShadowOperationError('startRemix', error);
				runShadowSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('error'));
				return { conversationId, admitted: false };
			}
		},
		[logShadowOperationError, onAdmissionRejected, runShadowSafely],
	);

	const reconcileIntent = useCallback(
		(intent: Exclude<PendingIntent, undefined>) => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}

			const currentPhase = phaseRef.current;
			if (intent === 'preview-opened' && currentPhase === 'ready') {
				pendingIntentRef.current = undefined;
				if (
					!runShadowSafely('sendPreviewOpened', () =>
						sendRemixEvent(activeRemixId, { type: 'PREVIEW_OPENED' }),
					)
				) {
					releaseLocalFlow();
					safelyEndRemix(activeRemixId, 'cancelled');
				}
				return;
			}

			if (intent === 'submitted' && (currentPhase === 'ready' || currentPhase === 'previewing')) {
				// Clear first so synchronous machine effects cannot finalize twice.
				releaseLocalFlow();
				const inserted = runShadowSafely('sendSubmissionEvents', () => {
					if (currentPhase === 'ready') {
						sendRemixEvent(activeRemixId, { type: 'PREVIEW_OPENED' });
					}
					sendRemixEvent(activeRemixId, { type: 'ACCEPTED' });
					sendRemixEvent(activeRemixId, { type: 'INSERTED' });
				});
				if (!inserted) {
					safelyEndRemix(activeRemixId, 'cancelled');
				}
			}
		},
		[releaseLocalFlow, runShadowSafely, safelyEndRemix],
	);

	const onPreviewOpened = useCallback(() => {
		if (remixIdRef.current && !finalizedRef.current) {
			if (pendingIntentRef.current !== 'submitted') {
				pendingIntentRef.current = 'preview-opened';
				reconcileIntent('preview-opened');
			}
		}
	}, [reconcileIntent]);

	const onSubmitted = useCallback(() => {
		if (remixIdRef.current && !finalizedRef.current) {
			pendingIntentRef.current = 'submitted';
			reconcileIntent('submitted');
		}
	}, [reconcileIntent]);

	const onPreviewDismissed = useCallback(() => {
		if (pendingIntentRef.current === 'submitted') {
			return;
		}
		endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	const onStopThinking = useCallback(() => {
		endWith({ event: 'GENERATION_ABORTED', reason: 'aborted' });
	}, [endWith]);

	const onRovoAborted = useCallback(() => {
		endWith({ event: 'GENERATION_ABORTED', reason: 'aborted' });
	}, [endWith]);

	const onErrorDismissed = useCallback(() => {
		endWith({ event: 'GENERATION_ERROR', reason: 'cancelled' });
	}, [endWith]);

	const onSendRejected = useCallback(() => {
		endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	const onNavigation = useCallback(() => {
		endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	useEffect(() => {
		const activeRemixId = remixIdRef.current;
		if (!activeRemixId || finalizedRef.current) {
			return;
		}

		if (phase === 'error' || phase === 'aborted') {
			releaseLocalFlow();
			safelyEndRemix(activeRemixId, phase === 'aborted' ? 'aborted' : 'cancelled');
			return;
		}

		const pendingIntent = pendingIntentRef.current;
		if (!pendingIntent) {
			return;
		}

		if (pendingIntent === 'preview-opened' && phase === 'ready') {
			pendingIntentRef.current = undefined;
			if (
				!runShadowSafely('sendPreviewOpened', () =>
					sendRemixEvent(activeRemixId, { type: 'PREVIEW_OPENED' }),
				)
			) {
				releaseLocalFlow();
				safelyEndRemix(activeRemixId, 'cancelled');
			}
			return;
		}

		if (pendingIntent === 'submitted' && (phase === 'ready' || phase === 'previewing')) {
			releaseLocalFlow();
			const inserted = runShadowSafely('sendSubmissionEvents', () => {
				if (phase === 'ready') {
					sendRemixEvent(activeRemixId, { type: 'PREVIEW_OPENED' });
				}
				sendRemixEvent(activeRemixId, { type: 'ACCEPTED' });
				sendRemixEvent(activeRemixId, { type: 'INSERTED' });
			});
			if (!inserted) {
				safelyEndRemix(activeRemixId, 'cancelled');
			}
		}
	}, [phase, releaseLocalFlow, runShadowSafely, safelyEndRemix]);

	// Component teardown mirrors legacy dismissal and releases machine admission.
	useEffect(() => {
		return () => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}
			releaseLocalFlow();
			runShadowSafely('sendUnmountCancelled', () =>
				sendRemixEvent(activeRemixId, { type: 'CANCELLED' }),
			);
			safelyEndRemix(activeRemixId, 'cancelled');
		};
	}, [releaseLocalFlow, runShadowSafely, safelyEndRemix]);

	return {
		startFlow,
		onPreviewOpened,
		onSubmitted,
		onPreviewDismissed,
		onStopThinking,
		onRovoAborted,
		onErrorDismissed,
		onSendRejected,
		onNavigation,
	};
};
